<template>
  <div class="page-container professionals-page">
    <portal to="page-name">Profissionais</portal>
    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Profissionais</h1>
      </div>
      <div class="card-body">
        <div class="columns">
          <div class="column form-group">
            <div class="input-group">
              <input
                type="text"
                class="form-input"
                v-model="filterSearch"
                @keydown.enter="find"
                :disabled="deleting"
                placeholder="Pesquisar por nome do profissional"
                maxlength="100"
                v-focus>
              <button class="btn btn-neutral btn-action input-group-btn btn-icon"
                      :disabled="deleting"
                      @click="find"
                      tabindex="-1">
                <fa-icon :icon="['fal', 'search']" />
              </button>
            </div>
          </div>
          <div class="column col-auto">
            <button class="btn btn-primary btn-icon btn-icon-left mr-2"
                    :disabled="deleting"
                    @click="create">
              <fa-icon :icon="['fal', 'plus']" />Novo profissional
            </button>
            <button class="btn btn-gray btn-icon btn-icon-left"
                    :class="{loading: printing}"
                    :disabled="printing || deleting"
                    @click="print">
              <fa-icon :icon="['fal', 'print']" />Imprimir
            </button>
          </div>
        </div>
        <div class="loading loading-lg mt-2" v-if="loading"></div>
        <template v-else>
          <template v-if="data.items.length === 0">
            <div class="empty mt-2">
              <div class="empty-icon">
                <fa-icon :icon="['fal', 'info-circle']" size="3x" />
              </div>
              <p class="empty-title h5">Profissionais</p>
              <p class="empty-subtitle">
                Nenhum profissional encontrado. Verifique os filtros para realizar novas buscas
              </p>
            </div>
          </template>
          <template v-else>
            <table class="table table-striped table-hover">
              <thead>
              <tr>
                <th class="hide-sm" style="width: 40px">#</th>
                <th>Nome</th>
                <th class="hide-sm hide-md">CPF</th>
                <th class="hide-sm hide-md">Conselho</th>
                <th>Especialidade</th>
                <th style="width: 70px" />
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, i) in data.items" :key="i">
                <td class="hide-sm">{{ i + 1 }}</td>
                <td>
                  <span class="hover-link" v-if="deleting">{{ item.name }}</span>
                  <router-link :to="editLink(item)" v-else>
                    {{ item.name }}
                  </router-link>
                </td>
                <td class="hide-sm hide-md">
                  {{ item.identity ? item.identity.value : '' | cpf }}
                </td>
                <td class="hide-sm hide-md">
                  <span v-if="item.professional.councils
                  && item.professional.councils.length > 0">
                    {{ getCouncil(item.professional.councils[0]) }}
                  </span>
                </td>
                <td>
                  <span v-if="item.professional.specialtyNames.length">
                    {{ item.professional.specialtyNames[0] }}
                    <span
                      v-if="item.professional.specialtyNames.length > 1"
                      class="label text-primary tooltip hover-link ml-1 "
                      :data-tooltip="item.professional.specialtyNames.slice(1).join('\n')">
                      +{{ item.professional.specialtyNames.length -1 }}
                    </span>
                  </span>
                </td>
                <td class="text-right">
                  <button class="btn btn-sm btn-action btn-icon btn-secondary tooltip mr-1"
                          :disabled="deleting"
                          @click="edit(item)" data-tooltip="Editar">
                    <fa-icon :icon="['fal', 'pencil']"/>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon btn-error tooltip"
                          :class="{loading: deleting && idx === i}"
                          :disabled="deleting"
                          @click="remove(item)" data-tooltip="Excluir">
                    <fa-icon :icon="['fal', 'times']"/>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </template>
        </template>
      </div>
      <div class="card-footer text-center" v-if="data.items.length > 0">
        <button
          v-if="data.hasMore && !loading"
          class="btn btn-primary btn-fixed-width"
          @click="loadMore"
          :disabled="loadingMore"
          :class="{ loading: loadingMore }"
        >Carregar mais</button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DELETE_PROFESSIONAL,
  PRINT_PROFESSIONAL_LIST,
} from '@/data/actions/modules/general';
import moment from 'moment';
import { mapState } from 'vuex';

export default {
  data() {
    return {
      idx: null,
      loading: false,
      loadingMore: false,
      printing: false,
      deleting: false,
      filterSearch: '',
      data: {
        items: [],
        hasMore: false,
        limit: 100,
        offset: 0,
      },
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    canAccessRemoveProfessional() {
      if (DELETE_PROFESSIONAL) {
        return this.$can(DELETE_PROFESSIONAL);
      }
      return true;
    },
    canAccessPrintProfessional() {
      if (PRINT_PROFESSIONAL_LIST) {
        return this.$can(PRINT_PROFESSIONAL_LIST);
      }
      return true;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    find() {
      this.data.offset = 0;
      this.load();
    },
    loadMore() {
      this.data.offset += this.data.limit;
      this.load();
    },
    load() {
      const firstData = this.data.offset === 0;

      if (firstData) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      const params = {
        limit: this.data.limit,
        offset: this.data.offset,
        active: true,
      };

      if (this.filterSearch.trim().length > 0) {
        params.search = this.filterSearch;
      }

      this.$http.get('/professionals', { params })
        .then(({ data }) => {
          data.items = data.items.map((item) => {
            item.professional.specialtyNames = item.professional.specialties
              .map(({ name }) => name);
            return item;
          });
          this.data.hasMore = data.hasMore;
          this.data.items = firstData ? data.items : [...this.data.items, ...data.items];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao carregar os dados!');
        })
        .finally(() => {
          this.loading = false;
          this.loadingMore = false;
        });
    },
    create() {
      this.$router.push('/clinical/professionals/create');
    },
    edit(item) {
      if (this.deleting) {
        return;
      }
      this.$router.push(`/clinical/professionals/${item.id}/edit`);
    },
    editLink(item) {
      return `/clinical/professionals/${item.id}/edit`;
    },
    remove(item) {
      if (!this.canAccessRemoveProfessional) {
        this.$toast.error('Acesso não autorizado!');
        return;
      }

      if (this.deleting) {
        return;
      }

      this.$dialog.show('', {
        size: 'md',
        html: `<div class="text-center">
                <p class="text-center h4">Atenção!</p>
                <p>Ao confirmar a remoção,
                 <strong class="text-primary">todos os valores</strong>
                 personalizados serão excluídos.</p>
                <p>Este registro também
                 <strong class="text-primary">será desvinculado</strong>
                 de todas as agendas e demais telas do sistema.</p>
                <div class="h6 text-error text-bold">
                 Essa ação é irreversível. Deseja continuar?
                </div>
                <br>
                <div>Responsável: <strong>${this.user.name}</strong></div>
                <div>
                  Data e hora: <strong>${moment().format('DD/MM/YYYY HH:mm')}</strong>
                </div>
              </div>`,
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.deleting = true;
              const idx = this.data.items.indexOf(item);
              this.idx = idx;
              close();
              this.$http
                .delete(`/professionals/${item.id}`)
                .then(() => {
                  this.data.items.splice(idx, 1);
                })
                .catch(() => {
                  this.$toast.error('Ocorreu um erro. Tente novamente!');
                })
                .finally(() => {
                  this.deleting = false;
                });
            },
          },
        ],
      });
    },
    print() {
      if (!this.canAccessPrintProfessional) {
        this.$toast.error('Acesso não autorizado!');
        return null;
      }

      if (this.printing) {
        return null;
      }

      this.printing = true;

      return this.$file
        .print('/professionals/print')
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao imprimir!');
        })
        .finally(() => {
          this.printing = false;
        });
    },
    getCouncil(item) {
      if (!item.name || !item.state || !item.record) {
        return '-';
      }
      return `${item.name}-${item.state} ${item.record}`;
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';
.professionals-page {
  .btn-fixed-width {
    font-size: $font-size-lg;
    height: 2.2rem;
    margin: $layout-spacing-lg 0;
    width: 11rem;
  }
}
</style>
